import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PayModal, usePayStore } from '@/components/common/pay';
import { fetchMembershipOrders, fetchPointOrders } from '@/service/api/membership';
import { useMembershipStore } from '@/stores/membershipStore';

// 统一订单显示模型
export interface DisplayOrder {
  id: string;
  order_no: string;
  order_type: 'membership' | 'point';
  title: string;
  description?: string;
  amount: string;
  currency?: string;
  payment_status: 'INITIATED' | 'PAYING' | 'SUCCEEDED' | 'FAILED' | 'CLOSED' | string;
  created_at: string;
  expires_at?: string;
  payment_channel?: 'WECHAT' | 'ALIPAY' | string;
  paid_at?: string;
}

// 格式化时间
function formatDate(dateStr?: string) {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

// 格式化支付状态转义及样式
function formatPaymentStatus(status?: string) {
  const s = (status || '').toUpperCase();
  switch (s) {
    case 'SUCCEEDED':
    case 'PAID':
      return { text: '交易成功', className: 'font-semibold text-emerald-600' };
    case 'INITIATED':
    case 'PAYING':
      return { text: '待支付', className: 'font-semibold text-amber-600' };
    case 'CLOSED':
      return { text: '已关闭', className: 'font-semibold text-slate-500' };
    case 'FAILED':
      return { text: '支付失败', className: 'font-semibold text-rose-500' };
    case 'EXPIRED':
      return { text: '已过期', className: 'font-semibold text-slate-400' };
    default:
      return { text: status || '--', className: 'font-semibold text-slate-600' };
  }
}

export function OrdersModule() {
  const openMembershipModal = useMembershipStore((state) => state.openMembershipModal);
  const [orders, setOrders] = useState<DisplayOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'membership' | 'point'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'succeeded' | 'closed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<DisplayOrder | null>(null);
  const [toastMsg, setToastMsg] = useState('');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setToastMsg('订单号已复制到剪贴板');
      setTimeout(() => setToastMsg(''), 2000);
    });
  };

  // 加载订单列表（整合会员订单 API 和积分订单 API）
  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const [memRes, pointRes] = await Promise.allSettled([
        fetchMembershipOrders({ page: 1, page_size: 50 }),
        fetchPointOrders({ page: 1, page_size: 50 }),
      ]);

      const fetchedList: DisplayOrder[] = [];

      if (memRes.status === 'fulfilled' && memRes.value?.items) {
        memRes.value.items.forEach((item) => {
          fetchedList.push({
            id: String(item.id || ''),
            order_no: item.order_no || String(item.id || ''),
            order_type: 'membership',
            title: '会员订阅订单',
            amount: item.amount || '0.00',
            currency: item.currency || 'CNY',
            payment_status: item.payment_status || item.status || 'INITIATED',
            created_at: item.created_at || '',
            expires_at: item.expires_at,
          });
        });
      }

      if (pointRes.status === 'fulfilled' && pointRes.value?.items) {
        pointRes.value.items.forEach((item) => {
          fetchedList.push({
            id: String(item.id || ''),
            order_no: item.order_no || String(item.id || ''),
            order_type: 'point',
            title: '积分充值订单',
            amount: item.amount || '0.00',
            currency: item.currency || 'CNY',
            payment_status: item.payment_status || item.status || 'INITIATED',
            created_at: item.created_at || '',
            expires_at: item.expires_at,
          });
        });
      }

      // 按时间倒序排列
      fetchedList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setOrders(fetchedList);
    } catch (err) {
      console.error('加载订单列表失败:', err);
      setLoadError('加载订单失败，请稍后重试');
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // 过滤后的订单列表
  const filteredOrders = useMemo(() => {
    return orders.filter((ord) => {
      // 1. 类型过滤
      if (categoryFilter !== 'all' && ord.order_type !== categoryFilter) {
        return false;
      }
      // 2. 状态过滤
      const s = (ord.payment_status || '').toUpperCase();
      const isPending = s === 'INITIATED' || s === 'PAYING';
      const isSucceeded = s === 'SUCCEEDED' || s === 'PAID';
      const isClosed = s === 'FAILED' || s === 'CLOSED' || s === 'EXPIRED';

      if (statusFilter === 'pending' && !isPending) return false;
      if (statusFilter === 'succeeded' && !isSucceeded) return false;
      if (statusFilter === 'closed' && !isClosed) return false;

      // 3. 关键词搜索
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesNo = ord.order_no.toLowerCase().includes(q);
        const matchesTitle = ord.title.toLowerCase().includes(q);
        if (!matchesNo && !matchesTitle) return false;
      }

      return true;
    });
  }, [orders, categoryFilter, statusFilter, searchQuery]);

  // 统计指标
  const stats = useMemo(() => {
    const totalCount = orders.length;
    const pendingCount = orders.filter(
      (o) => o.payment_status === 'INITIATED' || o.payment_status === 'PAYING'
    ).length;
    const succeededCount = orders.filter(
      (o) => o.payment_status === 'SUCCEEDED' || o.payment_status === 'PAID'
    ).length;
    const totalAmount = orders
      .filter((o) => o.payment_status === 'SUCCEEDED' || o.payment_status === 'PAID')
      .reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);

    return { totalCount, pendingCount, succeededCount, totalAmount: totalAmount.toFixed(2) };
  }, [orders]);

  // 唤起支付弹窗
  const handlePay = (order: DisplayOrder) => {
    usePayStore
      .getState()
      .createPaymentAndOpenModal(
        order.id,
        order.order_type,
        'WECHAT',
        order.amount
      );
  };

  return (
    <div className="min-w-0">
      {/* Toast 提示 */}
      {toastMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-full shadow-2xl animate-[fadeIn_0.2s_ease-out] flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-emerald-400 text-sm"></i>
          <span>{toastMsg}</span>
        </div>
      )}

      {/* 数据概览卡片区 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100/90 shadow-2xs">
          <p className="text-xs text-slate-500 font-medium mb-1">总订单数量</p>
          <p className="text-xl font-bold font-mono text-slate-900">{stats.totalCount}</p>
        </div>
        <div
          className={`rounded-2xl p-4 border transition-all ${
            stats.pendingCount > 0
              ? 'bg-amber-50/70 border-amber-200/80 shadow-2xs'
              : 'bg-slate-50/80 border-slate-100/90'
          }`}
        >
          <p className="text-xs text-amber-700 font-medium mb-1 flex items-center justify-between">
            <span>待支付订单</span>
            {stats.pendingCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
            )}
          </p>
          <p className="text-xl font-bold font-mono text-amber-600">{stats.pendingCount}</p>
        </div>
        <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100/90 shadow-2xs">
          <p className="text-xs text-slate-500 font-medium mb-1">完成交易数</p>
          <p className="text-xl font-bold font-mono text-emerald-600">{stats.succeededCount}</p>
        </div>
        <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100/90 shadow-2xs">
          <p className="text-xs text-slate-500 font-medium mb-1">累计消费金额</p>
          <p className="text-xl font-bold font-mono text-slate-900">
            <span className="text-xs font-normal mr-0.5 text-slate-500">¥</span>
            {stats.totalAmount}
          </p>
        </div>
      </div>

      {/* 筛选与搜索工具栏 */}
      <div className="bg-white rounded-2xl p-4 mb-6 space-y-3 border border-slate-200/80 shadow-2xs">
        {/* 第一行：订单类型 Tab 切换 */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
            {[
              { key: 'all', label: '全部类型' },
              { key: 'membership', label: '会员订阅' },
              { key: 'point', label: '积分充值' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setCategoryFilter(tab.key as any)}
                className={`text-xs px-3.5 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                  categoryFilter === tab.key
                    ? 'bg-white text-slate-900 shadow-2xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 搜索框 */}
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
            <input
              type="text"
              placeholder="搜索订单号或商品名称..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:bg-white focus:ring-1 focus:ring-amber-500/20 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
        </div>

        {/* 第二行：订单状态筛选按钮 */}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <span className="text-xs text-slate-400 font-medium mr-1">订单状态:</span>
          {[
            { key: 'all', label: '全部状态' },
            { key: 'pending', label: '待支付' },
            { key: 'succeeded', label: '已完成' },
            { key: 'closed', label: '已关闭 / 失败' },
          ].map((st) => (
            <button
              key={st.key}
              onClick={() => setStatusFilter(st.key as any)}
              className={`text-xs px-3 py-1 rounded-full transition-all cursor-pointer ${
                statusFilter === st.key
                  ? 'bg-slate-900 text-white font-semibold shadow-2xs'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200/60'
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
      </div>

      {/* 订单列表容器 */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200/80 shadow-2xs">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-emerald-200 border-t-emerald-500 animate-spin"></div>
            <p className="text-xs text-slate-500 font-medium">正在获取最新订单记录...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-12 bg-rose-50/50 rounded-2xl border border-rose-100">
            <i className="ri-error-warning-line text-2xl text-rose-400 mb-2 inline-block"></i>
            <p className="text-xs text-rose-600">{loadError}</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200/80 shadow-2xs">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">未找到匹配的订单记录</p>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mb-5">
              您可以尝试更换搜索关键词或重置筛选条件，也可前往会员中心选购合适套餐。
            </p>
            <button
              onClick={() => openMembershipModal()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-all shadow-sm cursor-pointer"
            >
              选购套餐服务
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        ) : (
          filteredOrders.map((ord) => {
            const statusUpper = (ord.payment_status || '').toUpperCase();
            const isPending = statusUpper === 'INITIATED' || statusUpper === 'PAYING';
            const isSucceeded = statusUpper === 'SUCCEEDED' || statusUpper === 'PAID';
            const isClosed = statusUpper === 'FAILED' || statusUpper === 'CLOSED' || statusUpper === 'EXPIRED';

            return (
              <div
                key={ord.id}
                className={`rounded-2xl p-5 border transition-all hover:shadow-md ${
                  isPending
                    ? 'border-amber-200/80 bg-amber-50/20'
                    : 'border-slate-200/70 bg-white shadow-2xs'
                }`}
              >
                {/* 头部：类型标签 + 订单号 + 状态 */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[11px] px-2.5 py-0.5 rounded-md font-semibold ${
                        ord.order_type === 'membership'
                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200/60'
                          : 'bg-amber-50 text-amber-800 border border-amber-200/60'
                      }`}
                    >
                      {ord.order_type === 'membership' ? '会员订阅' : '积分充值'}
                    </span>
                    <div className="flex items-center gap-1 text-xs font-mono text-slate-500">
                      <span>单号:</span>
                      <span className="font-semibold text-slate-800">{ord.order_no}</span>
                      <button
                        title="复制订单号"
                        onClick={() => copyToClipboard(ord.order_no)}
                        className="p-1 hover:text-emerald-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-file-copy-line text-xs"></i>
                      </button>
                    </div>
                  </div>

                  {/* 状态 Badge */}
                  <div>
                    {isPending && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                        待支付
                      </span>
                    )}
                    {isSucceeded && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
                        <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                        交易成功
                      </span>
                    )}
                    {isClosed && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">
                        <i className="ri-close-circle-line"></i>
                        已失效 / 已取消
                      </span>
                    )}
                  </div>
                </div>

                {/* 主内容区：商品名称、描述、金额 */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">
                      {ord.title}
                    </h3>
                    {ord.description && (
                      <p className="text-xs text-slate-500 line-clamp-1">{ord.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                      <span>下单时间：{formatDate(ord.created_at)}</span>
                      {ord.paid_at && <span>支付时间：{formatDate(ord.paid_at)}</span>}
                    </div>
                  </div>

                  {/* 金额展示 */}
                  <div className="text-left sm:text-right shrink-0">
                    <div className="text-xs text-slate-400 mb-0.5">订单金额</div>
                    <div className="text-xl font-bold font-mono text-slate-900">
                      <span className="text-xs font-semibold mr-0.5 text-emerald-600">¥</span>
                      {ord.amount}
                    </div>
                  </div>
                </div>

                {/* 底部操作工具栏 */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs">
                  <div className="text-slate-400">
                    {isPending && ord.expires_at && (
                      <span className="text-amber-700 flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        请在 30 分钟内完成支付
                      </span>
                    )}
                    {isSucceeded && ord.payment_channel && (
                      <span className="flex items-center gap-1 text-slate-500">
                        支付渠道：
                        {ord.payment_channel === 'WECHAT' ? (
                          <span className="text-emerald-600 font-medium">微信支付</span>
                        ) : (
                          <span className="text-sky-600 font-medium">支付宝</span>
                        )}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedOrderDetail(ord)}
                      className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium transition-all cursor-pointer"
                    >
                      订单详情
                    </button>

                    {isPending && (
                      <button
                        onClick={() => handlePay(ord)}
                        className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-all shadow-sm cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-qr-code-line"></i>
                        立即支付
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 订单详情弹窗 Drawer / Modal */}
      {selectedOrderDetail && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            onClick={() => setSelectedOrderDetail(null)}
          />
          <div className="relative z-10 w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 animate-[scaleIn_0.2s_ease-out]">
            <button
              onClick={() => setSelectedOrderDetail(null)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 p-1.5 rounded-full hover:bg-slate-100 transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                <i className="ri-file-text-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">订单明细</h3>
                <p className="text-xs text-slate-400">单号：{selectedOrderDetail.order_no}</p>
              </div>
            </div>

            <div className="space-y-3 bg-slate-50/80 p-4 rounded-2xl border border-slate-100 text-xs text-slate-600 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">商品名称</span>
                <span className="font-semibold text-slate-800">{selectedOrderDetail.title}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">订单类型</span>
                <span>{selectedOrderDetail.order_type === 'membership' ? '会员订阅' : '积分充值'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">订单金额</span>
                <span className="font-bold text-slate-900 text-sm font-mono">¥ {selectedOrderDetail.amount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">创建时间</span>
                <span>{formatDate(selectedOrderDetail.created_at)}</span>
              </div>
              {selectedOrderDetail.paid_at && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">支付时间</span>
                  <span>{formatDate(selectedOrderDetail.paid_at)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200/60">
                <span className="text-slate-400">支付状态</span>
                <span className={formatPaymentStatus(selectedOrderDetail.payment_status).className}>
                  {formatPaymentStatus(selectedOrderDetail.payment_status).text}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedOrderDetail(null)}
                className="w-full py-2.5 rounded-full border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-all"
              >
                关闭窗口
              </button>
              {(selectedOrderDetail.payment_status === 'INITIATED' ||
                selectedOrderDetail.payment_status === 'PAYING') && (
                <button
                  onClick={() => {
                    const ord = selectedOrderDetail;
                    setSelectedOrderDetail(null);
                    handlePay(ord);
                  }}
                  className="w-full py-2.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-500/20 hover:bg-emerald-600 transition-all"
                >
                  去支付
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 全站通用扫码支付 Modal */}
      <PayModal onSuccess={() => loadOrders()} />
    </div>
  );
}

export default OrdersModule;
